import { computed, type Ref } from 'vue'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { useHttp } from '@/shared/lib'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { ListFieldList } from '@/entities/Inventory/TypeField'

export const PRIMITIVE_FIELD_CLASS_ID = 1
export const CATALOG_FIELD_CLASS_ID = 2
export const REFERENCE_FIELD_CLASS_ID = 3

export const PASSPORT_BUILDER_OBJECT_TYPE_FIELDS_STALE_TIME = 30_000

export const passportBuilderObjectTypeFieldsQueryKey = (objectTypeId: number) => [
  'passport_builder_object_type_fields',
  objectTypeId,
]

export type PassportBuilderFieldTypeDto = {
  id: number
  name: string
}

export type PassportBuilderFieldClassDto = {
  id: number
  name: string
  types: PassportBuilderFieldTypeDto[]
}

export type PassportBuilderDictionaryDto = {
  id: number
  name: string
}

export type PassportBuilderFieldTableViewOrderDto = {
  row?: number | null
  column?: number | null
  colspan?: number | null
  rowspan?: number | null
  view_type?: 'value' | 'table' | null
  name?: string | null
  group_id?: number | null
}

export type PassportBuilderFieldPropsDto = {
  id?: number
  catalog_objects_type_id?: number
  catalog_objects_type_field_id?: number
  table_view_order?: PassportBuilderFieldTableViewOrderDto | null
  pseudo_sql?: string | null
  /** Размещение участвует в фильтре карты. */
  filter_map?: boolean
  /** Размещение доступно в расширенном поиске. */
  filter_search?: boolean
  /** Справочник значений для фильтра карты / расширенного поиска. */
  filter_dictionary_id?: number | null
}

export type PassportBuilderObjectTypeFieldDto = {
  id: number
  catalog_objects_type_id: number
  field_type: number
  name: string
  reverse_name?: string | null
  mirror_field?: { name?: string | null } | null
  catalog_id: number | null
  reference_id: number | null
  mirror_field_id: number | null
  default_value: string | null
  is_system: boolean
  is_required: boolean
  use_in_filter: boolean
  use_in_search: boolean
  validation_props: Record<string, unknown>
  sort: number
  field_props: PassportBuilderFieldPropsDto[] | PassportBuilderFieldPropsDto | null
}

export type PassportBuilderObjectTypeFieldPayload = Omit<
  PassportBuilderObjectTypeFieldDto,
  'id' | 'reverse_name' | 'mirror_field' | 'field_props'
> & {
  reverse_name?: string
}

export type PassportBuilderObjectTypeFieldDraft = {
  local_id: string
  id: number | null
  catalog_objects_type_id: number
  field_type: number | null
  name: string
  catalog_id: number | null
  reference_id: number | null
  mirror_field_id: number | null
  default_value: string
  is_system: boolean
  is_required: boolean
  use_in_filter: boolean
  use_in_search: boolean
  validation_props: Record<string, unknown>
  sort: number
}

export type ObjectTypeFieldsResponse = {
  fields: PassportBuilderObjectTypeFieldDto[]
  fieldClasses: PassportBuilderFieldClassDto[]
  dictionaries: PassportBuilderDictionaryDto[]
  fieldGroups: ObjectTypeGroup[]
}

const asRecord = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return {}
}

/**
 * Нормализует ответ useHttp: интерцептор переносит `payloads` в `payload`,
 * а `data` оставляет уже распакованным.
 */
export const normalizeObjectTypeFieldsResponse = (response: unknown): ObjectTypeFieldsResponse => {
  const raw = response as {
    data?: unknown
    payload?: {
      field_classes?: PassportBuilderFieldClassDto[]
      dictionaries?: PassportBuilderDictionaryDto[]
      field_groups?: ObjectTypeGroup[]
    }
    payloads?: {
      field_classes?: PassportBuilderFieldClassDto[]
      dictionaries?: PassportBuilderDictionaryDto[]
      field_groups?: ObjectTypeGroup[]
    }
  } | null
  const data = raw?.data as { fields?: unknown } | unknown[] | undefined
  const fields = Array.isArray(data)
    ? data
    : Array.isArray((data as { fields?: unknown })?.fields)
      ? ((data as { fields: PassportBuilderObjectTypeFieldDto[] }).fields)
      : []

  return {
    fields: fields as PassportBuilderObjectTypeFieldDto[],
    fieldClasses:
      raw?.payload?.field_classes ??
      raw?.payloads?.field_classes ??
      [],
    dictionaries:
      raw?.payload?.dictionaries ??
      raw?.payloads?.dictionaries ??
      [],
    fieldGroups:
      raw?.payload?.field_groups ??
      raw?.payloads?.field_groups ??
      [],
  }
}

/**
 * Возвращает плоский список всех типов полей из справочника классов.
 */
export const fieldTypesFromClasses = (
  fieldClasses: PassportBuilderFieldClassDto[],
): PassportBuilderFieldTypeDto[] => fieldClasses.flatMap((item) => item.types ?? [])

/**
 * Достает primitive-типы из справочника классов полей.
 */
export const primitiveFieldTypesFromClasses = (
  fieldClasses: PassportBuilderFieldClassDto[],
): PassportBuilderFieldTypeDto[] => {
  return fieldClasses.find((item) => item.id === PRIMITIVE_FIELD_CLASS_ID)?.types ?? []
}

/**
 * Достает catalog-тип справочников из справочника классов полей.
 */
export const catalogFieldTypesFromClasses = (
  fieldClasses: PassportBuilderFieldClassDto[],
): PassportBuilderFieldTypeDto[] => {
  return fieldClasses.find((item) => item.id === CATALOG_FIELD_CLASS_ID)?.types ?? []
}

/**
 * Достает reference-типы связей из справочника классов полей.
 */
export const referenceFieldTypesFromClasses = (
  fieldClasses: PassportBuilderFieldClassDto[],
): PassportBuilderFieldTypeDto[] => {
  return fieldClasses.find((item) => item.id === REFERENCE_FIELD_CLASS_ID)?.types ?? []
}

/**
 * Преобразует серверное поле в draft формы.
 */
export const objectTypeFieldToDraft = (
  field: PassportBuilderObjectTypeFieldDto,
): PassportBuilderObjectTypeFieldDraft => ({
  local_id: String(field.id),
  id: field.id,
  catalog_objects_type_id: field.catalog_objects_type_id,
  field_type: field.field_type,
  name: field.name ?? '',
  catalog_id: field.catalog_id ?? null,
  reference_id: field.reference_id ?? null,
  mirror_field_id: field.mirror_field_id ?? null,
  default_value: field.default_value ?? '',
  is_system: Boolean(field.is_system),
  is_required: Boolean(field.is_required),
  use_in_filter: Boolean(field.use_in_filter),
  use_in_search: Boolean(field.use_in_search),
  validation_props: asRecord(field.validation_props),
  sort: Number(field.sort) || 0,
})

/**
 * Создает пустой draft primitive-поля.
 */
export const createObjectTypeFieldDraft = (
  objectTypeId: number,
  fieldType: number | null,
  sort: number,
): PassportBuilderObjectTypeFieldDraft => ({
  local_id: `new_${crypto.randomUUID()}`,
  id: null,
  catalog_objects_type_id: objectTypeId,
  field_type: fieldType,
  name: '',
  catalog_id: null,
  reference_id: null,
  mirror_field_id: null,
  default_value: '',
  is_system: false,
  is_required: false,
  use_in_filter: false,
  use_in_search: false,
  validation_props: {},
  sort,
})

/**
 * Готовит payload для POST/PUT без `legacy_field_id`.
 */
export const objectTypeFieldDraftToPayload = (
  field: PassportBuilderObjectTypeFieldDraft,
  objectTypeId: number,
  index: number,
): PassportBuilderObjectTypeFieldPayload => ({
  catalog_objects_type_id: objectTypeId,
  field_type: Number(field.field_type),
  name: field.name.trim(),
  catalog_id: field.catalog_id,
  reference_id: field.reference_id,
  mirror_field_id: field.mirror_field_id,
  default_value: field.default_value || null,
  is_system: Boolean(field.is_system),
  is_required: Boolean(field.is_required),
  use_in_filter: Boolean(field.use_in_filter),
  use_in_search: Boolean(field.use_in_search),
  validation_props: asRecord(field.validation_props),
  sort: index + 1,
})

const REFERENCE_FIELD_TYPE_NAMES = new Set([
  'one_to_one',
  'one_to_many',
  'many_to_one',
  'many_to_many',
])

export const fieldTypeNameToLegacyType = (name: string): string => {
  if (name === 'catalog') return ListFieldList.LIST_TYPES
  if (REFERENCE_FIELD_TYPE_NAMES.has(name)) return 'link_object'
  if (name === 'iterator') return 'interator_simple'
  return name
}

const fieldInventoryType = (field: PassportBuilderObjectTypeFieldDto, typeName: string): number => {
  if (typeName === 'catalog') return field.catalog_id ?? 0
  if (field.reference_id != null) return field.reference_id
  return field.catalog_objects_type_id
}

const fieldPropsList = (field: PassportBuilderObjectTypeFieldDto): PassportBuilderFieldPropsDto[] => {
  if (Array.isArray(field.field_props)) return field.field_props
  if (field.field_props) return [field.field_props]
  return []
}

const fieldGroupId = (field: PassportBuilderObjectTypeFieldDto): number | undefined => {
  for (const props of fieldPropsList(field)) {
    const groupId = Number(props.table_view_order?.group_id)
    if (Number.isFinite(groupId) && groupId > 0) return groupId
  }
  return undefined
}

/**
 * Собирает DTO новых полей в старый формат, который пока использует палитра
 * конструктора паспорта.
 */
export const objectTypeFieldsToInventoryFields = (
  fields: PassportBuilderObjectTypeFieldDto[],
  fieldTypes: PassportBuilderFieldTypeDto[],
): ObjectInventoryField[] => {
  const typeById = new Map(fieldTypes.map((item) => [item.id, item.name]))

  return fields.map((field) => {
    const typeName = typeById.get(field.field_type) ?? 'string'
    const groupId = fieldGroupId(field)

    return {
      id: String(field.id),
      name: field.name,
      type: fieldTypeNameToLegacyType(typeName),
      items: [],
      object_type: fieldInventoryType(field, typeName),
      default_value: field.default_value ?? '',
      multiple: false,
      count_object: false,
      is_filtered: Boolean(field.use_in_filter),
      is_table: false,
      is_system: Boolean(field.is_system),
      is_global_search: Boolean(field.use_in_search),
      mandatory: Boolean(field.is_required),
      ...(groupId != null ? { group: groupId } : {}),
      ...(field.reference_id != null
        ? {
            link_target_type_id: field.reference_id,
            relation_field_id: field.id,
            cardinality: typeName,
          }
        : {}),
    }
  })
}

const SETTINGS_KEYS = ['length', 'min', 'max', 'precision', 'use_min', 'use_max'] as const
type SettingsKey = (typeof SETTINGS_KEYS)[number]

/**
 * Проецирует draft поля в legacy ObjectInventoryField для модалки настроек типа:
 * numeric field_type → строковый legacy type, скаляры ограничений — из validation_props.
 */
export const draftToLegacySettingsField = (
  draft: PassportBuilderObjectTypeFieldDraft,
  fieldTypes: PassportBuilderFieldTypeDto[],
): ObjectInventoryField => {
  const typeName = fieldTypes.find((item) => item.id === draft.field_type)?.name ?? 'string'
  const vp = draft.validation_props ?? {}

  return {
    id: draft.local_id,
    name: draft.name,
    type: fieldTypeNameToLegacyType(typeName),
    items: [],
    default_value: draft.default_value,
    object_type: 0,
    multiple: false,
    count_object: false,
    is_filtered: false,
    is_table: false,
    is_system: draft.is_system,
    is_global_search: draft.use_in_search,
    length: vp.length as number | undefined,
    min: vp.min as number | string | undefined,
    max: vp.max as number | string | undefined,
    precision: vp.precision as number | undefined,
    use_min: vp.use_min as boolean | undefined,
    use_max: vp.use_max as boolean | undefined,
    mandatory: draft.is_required,
  }
}

/**
 * Сливает скаляры ограничений из legacy-поля обратно в validation_props draft'а.
 * Вызывается при закрытии модалки; на Cancel значения те же (no-op), на Save — новые.
 */
export const mergeSettingsIntoDraftValidationProps = (
  draft: PassportBuilderObjectTypeFieldDraft,
  legacyField: ObjectInventoryField,
): void => {
  const next: Record<string, unknown> = { ...draft.validation_props }
  for (const key of SETTINGS_KEYS) {
    const value = legacyField[key as SettingsKey] as number | string | undefined
    if (value === undefined || value === null || value === '') {
      delete next[key]
    } else {
      next[key] = value
    }
  }
  draft.validation_props = next
}

/**
 * Загружает поля типа объекта из новой нормализованной модели.
 */
export const useLoadPassportBuilderObjectTypeFields = (
  objectTypeId: Ref<number>,
  enabled: Ref<boolean>,
) => {
  const http = useHttp()

  const queryKey = computed(() => passportBuilderObjectTypeFieldsQueryKey(objectTypeId.value))

  return useQuery({
    queryKey,
    queryFn: () => http.get(`/object-types/${objectTypeId.value}/fields`),
    retry: false,
    staleTime: PASSPORT_BUILDER_OBJECT_TYPE_FIELDS_STALE_TIME,
    enabled: () => enabled.value && objectTypeId.value > 0,
  })
}

/**
 * Создает поле типа объекта.
 */
export const useCreatePassportBuilderObjectTypeField = (objectTypeId: Ref<number>) => {
  const http = useHttp()

  return useMutation({
    mutationFn: (payload: PassportBuilderObjectTypeFieldPayload) =>
      http.post(`/object-types/${objectTypeId.value}/fields`, payload),
  })
}

/**
 * Обновляет поле типа объекта.
 */
export const useUpdatePassportBuilderObjectTypeField = (objectTypeId: Ref<number>) => {
  const http = useHttp()

  return useMutation({
    mutationFn: ({
      fieldId,
      payload,
    }: {
      fieldId: number
      payload: PassportBuilderObjectTypeFieldPayload
    }) => http.put(`/object-types/${objectTypeId.value}/fields/${fieldId}`, payload),
  })
}

/**
 * Удаляет поле типа объекта.
 */
export const useDeletePassportBuilderObjectTypeField = (objectTypeId: Ref<number>) => {
  const http = useHttp()

  return useMutation({
    mutationFn: (fieldId: number) =>
      http.del(`/object-types/${objectTypeId.value}/fields/${fieldId}`),
  })
}
